import React, { useEffect, useRef, useState, useCallback } from 'react';

import { AddressElement, PaymentElement } from '@stripe/react-stripe-js';

import {
  usePaymentStore,
  paymentStoreSelector,
} from '../../stores/paymentStore';
import RemoteDataStatus from '../../types/RemoteDataStatus';

import LoadingPlaceholder from './LoadingPlaceholder/LoadingPlaceholder';
import { PaymentFormContainer } from './styles';
import { PaymentFormProps } from './types';

/**
 * This component handles the display and functionality of the payment form using Stripe's PaymentElement and AddressElement.
 * It manages the loading and completion state of the payment and address forms.
 */
const PaymentForm: React.FC<PaymentFormProps> = ({
  options,
  onPaymentReady,
  onChange,
  layout,
  isDefaultCollapsed,
}) => {
  const [isLoadingPayment, setIsLoadingPayment] = useState(true);
  const [isLoadingAddress, setIsLoadingAddress] = useState(true);

  const [isPaymentComplete, setIsPaymentComplete] = useState(false);
  const [isAddressComplete, setIsAddressComplete] = useState(false);

  const addressElementWrapperRef = useRef<HTMLDivElement>(null);

  // Get the Stripe instance, payment status, payment actions, and billing info from the payment store
  const stripe = usePaymentStore(paymentStoreSelector.stripe);
  const paymentStatus = usePaymentStore(paymentStoreSelector.status);
  const paymentActions = usePaymentStore(paymentStoreSelector.actions);
  const billingInfo = usePaymentStore(paymentStoreSelector.billingInfo);

  useEffect(() => {
    onChange(isPaymentComplete && isAddressComplete);
  }, [isPaymentComplete, isAddressComplete, onChange]);

  useEffect(() => {
    if (!isLoadingPayment && !isLoadingAddress) {
      onPaymentReady();
    }
  }, [isLoadingPayment, isLoadingAddress, onPaymentReady]);

  const handleLoadError = (): void => {
    paymentActions.setStatus(RemoteDataStatus.Error, 'loadStripe');
  };

  // scroll address section inside its container based on provided position value
  const scrollAddressSection = useCallback(
    (position: 'start' | 'end'): void => {
      setTimeout(() => {
        addressElementWrapperRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: position,
        });
      }, 500);
    },
    [addressElementWrapperRef]
  );

  useEffect(() => {
    if (isPaymentComplete) {
      scrollAddressSection('start');
    }
  }, [isPaymentComplete, addressElementWrapperRef, scrollAddressSection]);

  // Render the payment form or loading placeholder
  let content;
  if (!stripe) {
    content = <LoadingPlaceholder backgroundStyle="transparent" />;
  } else {
    content = (
      <>
        {isLoadingPayment && (
          <LoadingPlaceholder backgroundStyle="transparent" />
        )}
        <PaymentElement
          options={{
            ...options,
            layout: {
              type: layout ?? 'tabs',
              defaultCollapsed: isDefaultCollapsed ?? true,
              ...(typeof options?.layout === 'object' ? options.layout : {}),
            },
            readOnly: paymentStatus !== RemoteDataStatus.Done,
          }}
          onChange={(event): void => setIsPaymentComplete(event.complete)}
          onLoadError={handleLoadError}
          onReady={(): void => setIsLoadingPayment(false)}
        />
        {isPaymentComplete && (
          <div ref={addressElementWrapperRef} style={{ marginTop: '10px' }}>
            <AddressElement
              options={{
                mode: 'billing',
                defaultValues: billingInfo || undefined,
              }}
              onChange={(event): void => {
                const postalCodeIsFilled =
                  event?.value?.address?.postal_code?.length > 0;
                const addressLine1IsFilled =
                  event?.value?.address?.line1?.length > 2;

                if (postalCodeIsFilled) {
                  // align address section to bottom of visible container section by auto-scrolling
                  scrollAddressSection('end');
                } else if (addressLine1IsFilled) {
                  // align address section to top of visible container section by auto-scrolling
                  scrollAddressSection('start');
                }
                setIsAddressComplete(event.complete);
              }}
              onLoadError={handleLoadError}
              onReady={(element): void => {
                element.focus();
                setIsLoadingAddress(false);
              }}
            />
          </div>
        )}
      </>
    );
  }

  return <PaymentFormContainer>{content}</PaymentFormContainer>;
};

export default PaymentForm;
